<template>
  <main class="account-assets-page">
    <AppNavBar :title="t('home.accountAssets')" background-color="#ffffff">
      <template #right>
        <button
          v-if="holderStatus === 3"
          type="button"
          class="nav-add-card-btn"
          @click="goCardDetail"
        >
          <!-- <app-image :src="addCardIcon" alt="" /> -->
        </button>
      </template>
    </AppNavBar>

    <van-pull-refresh v-model="refreshing" @refresh="handleRefresh">
      <section class="account-assets-content">
        <button type="button" class="asset-summary">
          <span class="asset-summary__label">{{
            t("wallet.propertyBalance")
          }}</span>
          <div class="asset-summary__amount">
            <span class="asset-summary__amount-value">{{ totalText }}</span>
            <span class="asset-summary__amount-type">{{ computeType }}</span>
          </div>
        </button>

        <section class="tool-grid">
          <button
            v-for="item in tools"
            :key="item.key"
            type="button"
            class="tool-grid__item"
            @click="runTool(item.key)"
          >
            <app-image :src="item.icon" alt="" />
            <span>{{ item.label }}</span>
          </button>
        </section>

        <section class="asset-list-section">
          <h2>{{ t("wallet.fundingAccount") }}</h2>

          <article
            v-for="(item, index) in assets"
            :key="
              item.contractAddress ||
              item.symbolType ||
              item.symbol ||
              item.coin ||
              index
            "
            class="asset-row"
          >
            <div class="asset-row__left">
              <app-image
                v-if="item.logo || item.img"
                :src="item.logo || item.img"
                alt=""
              />
              <span v-else class="asset-row__placeholder" />
              <div data-strong>{{ item.symbol || item.coin || "--" }}</div>
            </div>

            <div class="asset-row__right">
              <div data-strong>
                {{ item.balanceNormal || item.balance || "0" }}
              </div>
              <span>{{ assetQuoteText(item) }}</span>
            </div>
          </article>
        </section>
      </section>
    </van-pull-refresh>

    <van-popup
      v-model:show="showQuotePopup"
      position="bottom"
      round
      class="quote-popup"
    >
      <section class="quote-popup__panel">
        <header class="quote-popup__header">
          <span class="quote-popup__spacer" />
          <h3>{{ t("wallet.choosePricingFiat") }}</h3>
          <button
            type="button"
            class="quote-popup__close"
            @click="showQuotePopup = false"
          >
            <app-image class="app-cancel-icon" :src="cancelIcon" alt="" />
          </button>
        </header>

        <button
          v-for="item in quoteOptions"
          :key="item.value"
          type="button"
          class="quote-popup__option"
          :class="{ 'is-active': computeType === item.value }"
          @click="selectQuote(item.value)"
        >
          <span>{{ item.value }}</span>
          <van-icon name="success" size="18" />
        </button>
      </section>
    </van-popup>
  </main>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { closeToast, showFailToast, showLoadingToast, showToast } from "vant";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { devicesInfo } from "@/api/auth";
import { pcCardCreatePre } from "@/api/card";
import { pcCardAssetList } from "@/api/wallet";
import { useProfileStore } from "@/stores/profile";
import { useWalletTokenStore } from "@/stores/wallet-token";
import { formatMoney } from "@/utils/format";
import { navigateWithState } from "@/utils/route-state";
import { getDeviceId } from "@/utils/device";
import topupIcon from "@/assets/images/homeImage/topup_icon.png";
import withdrawIcon from "@/assets/images/homeImage/withdraw_icon.png";
import transferIcon from "@/assets/images/homeImage/transfer_icon.png";
import billIcon from "@/assets/images/homeImage/wastebook_icon.png";
import addCardIcon from "@/assets/images/cardImage/add_card_icon.png";
import cancelIcon from "@/assets/images/cancel_gray.png";

const { t } = useI18n();
const router = useRouter();
const profileStore = useProfileStore();
const walletTokenStore = useWalletTokenStore();

const refreshing = ref(false);
const showQuotePopup = ref(false);
const computeType = ref("USDT");
const usdToHkdPrice = ref(0);
const totalTo = ref(0);
const assets = ref([]);
const holderStatus = ref(0);

const tools = computed(() => [
  { key: "topup", label: t("home.topup"), icon: topupIcon },
  { key: "withdraw", label: t("home.withdraw"), icon: withdrawIcon },
  { key: "transfer", label: t("home.transfer"), icon: transferIcon },
  { key: "bill", label: t("home.bill"), icon: billIcon },
]);

const quoteOptions = [{ value: "HKD" }, { value: "USD" }];

const totalText = computed(() => {
  const amount =
    computeType.value === "USDT"
      ? totalTo.value
      : totalTo.value * usdToHkdPrice.value;
  return formatMoney(amount);
});

function assetQuoteText(item) {
  const balanceTo = Number(item?.balanceTo || 0);
  const amount =
    computeType.value === "USDT" ? balanceTo : balanceTo * usdToHkdPrice.value;
  return `${formatMoney(amount)} ${computeType.value}`;
}

function selectQuote(value) {
  computeType.value = value;
  showQuotePopup.value = false;
}

function goCardDetail() {
  navigateWithState(router, "/card/index", {
    namespace: "home-card",
    payload: { leftArrow: true },
  });
}

async function ensureTopupAccess() {
  let errorMessage = "";
  showLoadingToast({
    message: t("common.loading"),
    forbidClick: true,
    duration: 0,
  });
  try {
    const result = await devicesInfo({ token: getDeviceId() });
    if (Number(result?.isBind || 0) === 1) {
      router.push("/wallet/topup");
      return;
    }
    navigateWithState(router, "/auth/bind-email", {
      namespace: "auth-bind-email",
      payload: {
        back: "/wallet/account-assets",
      },
    });
  } catch (error) {
    errorMessage = error?.message || error?.msg || t("common.retryLater");
  } finally {
    closeToast();
  }
  if (errorMessage) {
    showFailToast(errorMessage);
  }
}

function runTool(key) {
  if (key === "topup") {
    void ensureTopupAccess();
    return;
  }
  if (key === "withdraw") {
    router.push("/wallet/withdraw");
    return;
  }
  if (key === "bill") {
    router.push("/wallet/bill");
    return;
  }
  showToast(t("home.actionTodo"));
}

async function loadPageData(showLoading = true) {
  let errorMessage = "";
  const address = profileStore.platformAddress || null;
  if (!address) {
    assets.value = [];
    totalTo.value = 0;
    usdToHkdPrice.value = 0;
    holderStatus.value = 0;
    return;
  }

  try {
    const [assetResult, preResult] = await Promise.all([
      pcCardAssetList({ address }, { meta: { showLoading: false } }),
      pcCardCreatePre({ address }, { meta: { showLoading: false } }),
    ]);
    const rows = Array.isArray(assetResult?.list) ? assetResult.list : [];
    assets.value = rows;
    totalTo.value = Number(assetResult?.totalTo || 0);
    usdToHkdPrice.value = Number(assetResult?.usdToHkdPrice || 0);
    holderStatus.value = Number(preResult?.holderStatus || 0);
    walletTokenStore.setAssetList(
      rows,
      profileStore.currentNetwork?.chainNum || 56,
    );
  } catch (error) {
    assets.value = [];
    totalTo.value = 0;
    usdToHkdPrice.value = 0;
    holderStatus.value = 0;
    if (showLoading) {
      errorMessage = error?.message || error?.msg || t("common.retryLater");
    }
  } finally {
    if (showLoading) {
      closeToast();
    }
  }
  if (errorMessage) {
    showFailToast(errorMessage);
  }
}

async function handleRefresh() {
  await loadPageData(false);
  refreshing.value = false;
}

onMounted(() => {
  void loadPageData(true);
});
</script>

<style scoped lang="scss">
.account-assets-page {
  min-height: var(--app-viewport-height);
  background: #ffffff;
}

.account-assets-content {
  padding: 16px 15px 28px;
}

.nav-add-card-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  background: transparent;

  .app-image {
    width: 24px;
    height: 24px;
    object-fit: contain;
  }
}

.asset-summary {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 8px 0 0;
  border: 0;
  background: transparent;
}

.asset-summary__label {
  color: #333333;
  font-size: 14px;
  line-height: 22px;
}

.asset-summary__amount {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  color: #000000;
  line-height: 1;
}

.asset-summary__amount-value {
  color: #000000;
  font-size: 16px;
  line-height: 24px;
  font-weight: 700;
}

.asset-summary__amount-type {
  color: #000000;
  font-size: 16px;
  line-height: 24px;
  font-weight: 700;
}

.asset-summary__quote {
  display: inline-flex;
  align-items: center;
  margin-top: 4px;
  color: #000000;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
}

.tool-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  margin-top: 34px;
}

.tool-grid__item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  min-width: 0;
  padding: 0;
  border: 0;
  background: transparent;

  .app-image {
    width: 48px;
    height: 48px;
    object-fit: contain;
  }

  span {
    margin-top: 6px;
    color: #1d2129;
    font-size: 13px;
    line-height: 20px;
    text-align: center;
  }
}

.asset-list-section {
  margin-top: 24px;

  h2 {
    margin: 0 0 10px;
    color: #000000;
    font-size: 20px;
    font-weight: 700;
    line-height: 28px;
  }
}

.asset-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
  padding: 12px 16px;
  // box-shadow: inset 0 0 0 1px #f2f3f5;
  border-radius: 10px;
  background: #ffffff;
}

.asset-row__left {
  display: flex;
  align-items: center;
  min-width: 0;
  flex: 1;

  .app-image,
  .asset-row__placeholder {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    flex: 0 0 34px;
  }

  .app-image {
    object-fit: cover;
  }

  [data-strong] {
    margin-left: 8px;
    color: #000000;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
  }
}

.asset-row__placeholder {
  background: #f5f5f5;
}

.asset-row__right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-left: 16px;

  [data-strong] {
    color: #000000;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
  }

  span {
    margin-top: 5px;
    color: rgba(0, 0, 0, 0.65);
    font-size: 12px;
    line-height: 18px;
    text-align: right;
  }
}

.quote-popup__panel {
  padding: 16px 16px calc(16px + env(safe-area-inset-bottom));
  background: #ffffff;
}

.quote-popup__header {
  display: flex;
  align-items: center;
  justify-content: space-between;

  h3 {
    margin: 0;
    color: #000000;
    font-size: 18px;
    font-weight: 700;
    line-height: 28px;
  }
}

.quote-popup__spacer {
  width: 24px;
  height: 24px;
}

.quote-popup__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  background: transparent;

  .app-image {
    width: 16px;
    height: 16px;
    object-fit: contain;
  }
}

.quote-popup__option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-top: 16px;
  padding: 18px 16px;
  border: 1px solid #c9cdd4;
  border-radius: 8px;
  background: transparent;
  color: #1d2129;
  font-size: 15px;
  line-height: 22px;

  .van-icon {
    color: transparent;
  }

  &.is-active {
    border-color: var(--wp-color-theme);
    background: #e8f3ff;

    .van-icon {
      color: var(--wp-color-theme);
    }
  }
}
</style>
